import React, { useMemo, useRef, useState } from 'react';

import { useClassName, useWindowSize } from 'common/hooks';
import { Pagination } from 'public/components';
import { ControlledTabs } from 'public/components/Tabs';
import { scrollTo } from 'utils/helpers/scroll';
import { CTA_TYPES, STANDARD_EVENTS } from 'utils/analytics/trackV2';
import { StickyDropDown } from 'public/components/StickyDropdown';

import Center from '../Center';
import Card from '../Card';
import Carousel from '../Carousel';
import useCustomStyle from '../../hooks';
import Link from '../Link';
import {
  appendStylesByPrefix,
  getStylesByPrefix,
  toStyleObject,
} from '../../utils';

import './list.less';

function getPageSize(value, itemCount, fallback = 1) {
  if (value === 'all') return itemCount;

  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function getControlledTabStyles(style) {
  return Object.fromEntries(
    Object.entries(style)
      .filter(([key]) => key.startsWith('--tab-'))
      .map(([key, value]) => {
        const name = key.replace('--tab-', '');
        const selected = name.startsWith('selected-');
        const property = selected ? name.replace('selected-', '') : name;
        const reactProperty = Object.keys(
          toStyleObject({ [property]: value })
        )[0];

        return [`${selected ? 'selected-' : ''}${reactProperty}`, value];
      })
  );
}

function ListWithTabs({ component, refs, venue, handleTrack }) {
  const classNames = useClassName('CLPList');
  const [tagIndex, setTagIndex] = useState(0);
  const [page, setPage] = useState(1);
  const ref = useRef();
  const { isMobile } = useWindowSize();
  const [setRef, style] = useCustomStyle(component.fields.settings);
  const variant = style['--variant'];
  const arrowTabInverted = Boolean(style['--arrow-tab-inverted']);
  const paginationColorInverted = style['--pagination-color-inverted']
    ? true
    : false;

  const type = component.fields.type || 'default';

  const tags = [
    ...new Set(component.fields.items.map((a) => a.fields.tags).flat()),
  ].sort(function (a, b) {
    if (!component?.fields?.tagOrder?.length) return a.localeCompare(b);

    return (
      component.fields.tagOrder.indexOf(a) -
      component.fields.tagOrder.indexOf(b)
    );
  });
  const items = component.fields.items.filter((item) =>
    item.fields?.tags?.includes(tags[tagIndex])
  );
  const pageSize = getPageSize(style['--page-size'], items.length);
  const pagedItems = items.filter((item, index) => {
    const indexMin = (page - 1) * pageSize;
    const indexMax = indexMin + pageSize;
    return index >= indexMin && index < indexMax;
  });
  const totalPages = Math.ceil(items.length / pageSize);
  const mobileSticky = component.fields.mobileSticky;
  const tabsVariant = style['--tabs-variant'];
  const useEventCards = tabsVariant === 'event-cards';
  const tabItems = tags.map((tag) => {
    const item = component.fields.items.find((candidate) =>
      candidate.fields?.tags?.includes(tag)
    );

    return {
      tag,
      description: item?.fields?.tabDescription,
      icon: item?.fields?.icon,
    };
  });
  const listVariants = [
    useEventCards && 'event-cards',
    variant && `variant-${variant}`,
  ].filter(Boolean);

  return (
    <div
      className={listVariants.length ? classNames(listVariants) : undefined}
      ref={(node) => {
        ref.current = node;
        setRef(node);
      }}>
      {isMobile && mobileSticky ? (
        <StickyDropDown
          options={tags}
          selectedIndex={tagIndex}
          parentRef={ref}
          handleChange={(tagName) => {
            setTagIndex(tags.findIndex((tag) => tag === tagName));
            setPage(1);
          }}
        />
      ) : (
        <Center>
          <ControlledTabs
            names={tags}
            selectedIndex={tagIndex}
            styles={{
              container: useEventCards
                ? classNames(['tabs', 'tabs-event-cards'])
                : classNames('tabs'),
            }}
            tabClassName={useEventCards ? classNames('event-tab') : undefined}
            renderTab={
              useEventCards
                ? (tagName, index) => {
                    const tab = tabItems[index];

                    return (
                      <>
                        {tab?.icon?.fields?.file?.url && (
                          <img
                            src={tab.icon.fields.file.url}
                            alt=""
                            aria-hidden="true"
                            className={classNames('event-tab-icon')}
                          />
                        )}
                        <span className={classNames('event-tab-title')}>
                          {tagName}
                        </span>
                        {tab?.description && (
                          <span className={classNames('event-tab-description')}>
                            {tab.description}
                          </span>
                        )}
                      </>
                    );
                  }
                : undefined
            }
            tabStyles={getControlledTabStyles(style)}
            tabsContainerStyles={toStyleObject(
              Object.fromEntries(
                Object.entries(style)
                  .filter(([key]) => key.startsWith('--tabs-container'))
                  .map(([key, value]) => [
                    key.replace('--tabs-container-', ''),
                    value,
                  ])
              )
            )}
            arrowTabInverted={arrowTabInverted}
            onClick={(tagName) => {
              setTagIndex(tags.findIndex((tag) => tag === tagName));
              setPage(1);
            }}
            handleTrack={handleTrack}
          />
        </Center>
      )}

      <Center
        className={classNames([
          'container',
          style['--layout'] === 'flex' ? 'container-flex' : `container-${type}`,
        ])}
        ref={setRef}
        style={{
          justifyContent: style['--justify-content'] || undefined,
        }}>
        {pagedItems.map((item) => {
          if (item.sys.contentType.sys.id === 'lpCarousel') {
            return (
              <Carousel
                key={item.sys.id}
                component={item}
                refs={refs}
                handleTrack={handleTrack}
              />
            );
          }

          return (
            <Card
              key={item.sys.id}
              item={appendStylesByPrefix(component, item, '--card-')}
              refs={refs}
              venue={venue}
              handleTrack={handleTrack}
            />
          );
        })}
      </Center>
      {totalPages > 1 && (
        <Center>
          <Pagination
            totalPages={Math.ceil(items.length / pageSize)}
            activePage={page}
            onPageChange={(e, data) => {
              setPage(data.activePage);

              scrollTo(ref.current, -138);
            }}
            className={classNames([
              'pagination',
              `pagination-color-${
                paginationColorInverted ? 'inverted' : 'default'
              }`,
            ])}
          />
        </Center>
      )}
    </div>
  );
}

function ListWithoutTabs({ component, refs, venue, handleTrack }) {
  const classNames = useClassName('CLPList');
  const [setRef, style] = useCustomStyle(component.fields.settings);
  const pageSize = getPageSize(
    style['--page-size'],
    component.fields.items.length
  );
  const borderRadius = style['--border-radius'];
  const transition = style['--transition'];
  const transform = style['--transform'];
  const order = style['--order'] || undefined;
  // Opt-in: pulls the first N items out into a separate labeled box above
  // the regular list (e.g. "Choose between" DreamWorks/Nickelodeon). No
  // effect unless a Contentful entry sets --highlight-count.
  const highlightCount = parseInt(style['--highlight-count'], 10) || 0;
  const highlightLabel = style['--highlight-label'];
  const sortAlphabetically = style['--alphabetical-sort'];
  const itemsStyles = {
    marginBottom: style['--margin-bottom'] || 'auto',
    marginTop: style['--margin-top'] || 'auto',
    order,
  };

  const [page, setPage] = useState(1);

  const processedItems = useMemo(() => {
    if (!sortAlphabetically) {
      return component.fields.items;
    }

    const result = [...component.fields.items];
    const firstPage = result.slice(0, pageSize);
    const rest = result
      .slice(pageSize)
      .sort((a, b) => a.fields.name.localeCompare(b.fields.name));

    return [...firstPage, ...rest];
  }, [component.fields.items, pageSize, sortAlphabetically]);

  const pagedItems = processedItems.slice(0, page * pageSize);

  const type = component.fields.type || 'default';
  const containerInnerCenter = style['--container-inner-center'] === 'true';
  const containerInnerBottom = style['--container-inner-bottom'];
  const containerInnerWidth = style['--container-inner-width'];
  const containerInnerBorderRadius = style['--container-inner-border-radius'];
  const containerInnerBackgroundColor =
    style['--container-inner-background-color'] || 'white';
  const containerInnerPadding = style['--container-inner-padding'];
  const containerInnerMaxWidth = style['--container-inner-max-width'];
  const wrapperStyles = getStylesByPrefix('--wrapper-', style);

  const containerStyles = !containerInnerCenter
    ? {}
    : {
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: containerInnerBottom,
        width: containerInnerWidth,
        borderRadius: containerInnerBorderRadius,
        backgroundColor: containerInnerBackgroundColor,
        padding: containerInnerPadding,
        maxWidth: containerInnerMaxWidth,
      };

  const renderItem = (item) => {
    if (item.sys.contentType.sys.id === 'lpLink') {
      return (
        <Link
          key={item.sys.id}
          component={appendStylesByPrefix(component, item, '--link-')}
          refs={refs}
          handleTrack={handleTrack}
        />
      );
    }

    return (
      <Card
        key={item.sys.id}
        item={appendStylesByPrefix(component, item, '--card-')}
        refs={refs}
        venue={venue}
        handleTrack={handleTrack}
      />
    );
  };

  const highlightedItems =
    highlightCount > 0 ? pagedItems.slice(0, highlightCount) : [];
  const remainingItems = pagedItems.slice(highlightedItems.length);

  return (
    <>
      {highlightedItems.length > 0 && (
        <div className={classNames('highlight-group')} ref={setRef}>
          {highlightLabel && (
            <div className={classNames('highlight-label')}>
              {highlightLabel}
            </div>
          )}
          <div className={classNames('highlight-items')}>
            {highlightedItems.map(renderItem)}
          </div>
        </div>
      )}
      <Center
        style={{
          ...wrapperStyles,
          ...containerStyles,
          justifyContent: style['--justify-content'] || undefined,
          ...itemsStyles,
        }}
        className={classNames([
          'container',
          style['--layout'] === 'flex' ? 'container-flex' : `container-${type}`,
        ])}
        ref={setRef}>
        {remainingItems.map(renderItem)}
      </Center>
      {pagedItems.length < processedItems.length && (
        <Center className={classNames('explore-more')}>
          <button
            className={classNames('explore-more')}
            style={{ borderRadius, transition, transform }}
            onClick={() => {
              if (handleTrack) {
                handleTrack({
                  event: STANDARD_EVENTS.click,
                  params: {
                    label: 'Explore More',
                    cta_type: CTA_TYPES.showMore,
                  },
                });
              }
              setPage((page) => page + 1);
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = style['--hover-bg'])
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = 'black')
            }>
            Explore More
          </button>
        </Center>
      )}
    </>
  );
}

export default function List({ component, refs, venue, handleTrack }) {
  if (component.fields.groupTags) {
    return (
      <ListWithTabs
        component={component}
        refs={refs}
        venue={venue}
        handleTrack={handleTrack}
      />
    );
  }

  return (
    <ListWithoutTabs
      component={component}
      refs={refs}
      venue={venue}
      handleTrack={handleTrack}
    />
  );
}
